import { DownOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space, Spin, Tooltip, Typography } from 'antd';
import cn from 'classnames';
import { useCallback, useEffect, useState } from 'react';

import {
  PaymentModalTypeEnum,
  type AcquiringPaymentButtonProps,
} from '@app/components/RecordModal/components/RecordPayment/types';
import { YOOKASSA } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import { useLazyGetPaymentMethodsQuery } from '@app/services/rtkQuery/SocialIntegrations/api';
import { SocialIntegrationIcon } from '@app/shared/ui';

import '@app/components/RecordModal/components/RecordDetails/styles/PaymentButton.desktop.scss';

import type { MenuProps } from 'antd';

const { Text } = Typography;
type YooKassaMenuItem = {
  key: string;
  label: string;
};

export const AcquiringPaymentButton: React.FC<AcquiringPaymentButtonProps> = ({
  method,
  isPayFormYooKassaButtonDisabled,
  handleOptionClick,
}) => {
  const [items, setItems] = useState<YooKassaMenuItem[]>([]);
  const [yooKassaSelectedPaymentMethod, setYooKassaSelectedPaymentMethod] =
    useState<YooKassaMenuItem | null>(null);

  const { activeBranchId: ActiveBranchId } = useUserPrint();

  const [getPaymentMethods, { isFetching: isGetPaymentMethodsLoading }] =
    useLazyGetPaymentMethodsQuery();

  const { showNotification } = useNotification();

  const onMenuClick: MenuProps['onClick'] = (e) => {
    const selectedItem = items.find((item) => item && item.key === e.key);
    if (selectedItem) {
      setYooKassaSelectedPaymentMethod(selectedItem);
    }
  };

  const fetchItems = useCallback(
    async (isNeedToSetDefaultPaymentMethod: boolean) => {
      try {
        const response = await getPaymentMethods({
          ActiveBranchId,
          paymentAccountId: method.Id,
        }).unwrap();

        const defaultPaymentMethod = response.find((item) => item.IsDefault);
        if (isNeedToSetDefaultPaymentMethod && defaultPaymentMethod) {
          setYooKassaSelectedPaymentMethod({
            key: defaultPaymentMethod.Code,
            label: defaultPaymentMethod.Name,
          });
        }
        setItems(
          response.map((item) => ({
            key: item.Code,
            label: item.Name,
          }))
        );
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    },
    [ActiveBranchId, getPaymentMethods, method.Id, showNotification]
  );

  useEffect(() => {
    fetchItems(true);
  }, [fetchItems]);

  return (
    <Space.Compact className={'payments__payments-buttons-with-dropdown'}>
      <Button
        className="payment-balance yoo-kassa-button"
        data-testid="yoo-kassa-payment"
        disabled={isPayFormYooKassaButtonDisabled}
        onClick={() => {
          if (!yooKassaSelectedPaymentMethod) {
            return showNotification({
              type: 'warning',
              message: 'Пожалуйста, выберите способ оплаты',
            });
          }
          handleOptionClick(PaymentModalTypeEnum.YOO_KASSA)({
            receipt: method,
            yooKassaPaymentMethodCode: yooKassaSelectedPaymentMethod.key,
          });
        }}
      >
        <div className="payment-receipt payment-component__buttons">
          <SocialIntegrationIcon
            type={YOOKASSA}
            size={24}
            style={{ marginRight: '8px', borderRadius: '4px' }}
          />
          <div className="receipt-title-container">
            <Tooltip title={method.Name}>
              <Text
                ellipsis
                className={cn('acquiring-receipt-title', {
                  'disable-acquiring-text': isPayFormYooKassaButtonDisabled,
                })}
              >
                {method.Name}
              </Text>
            </Tooltip>
            <p className="receipt-description">
              {yooKassaSelectedPaymentMethod?.label || 'Выберите способ оплаты'}
            </p>
          </div>
        </div>
      </Button>
      <Dropdown
        className="acquiring-dropdown"
        disabled={isPayFormYooKassaButtonDisabled}
        menu={{
          items: isGetPaymentMethodsLoading
            ? [
                {
                  key: 'loading',
                  label: <Spin size="small" />,
                  disabled: true,
                },
              ]
            : (items as MenuProps['items']),
          onClick: onMenuClick,
        }}
        placement="topRight"
        onOpenChange={(open) => {
          if (open) {
            fetchItems(false);
          }
        }}
      >
        <Button icon={<DownOutlined />} />
      </Dropdown>
    </Space.Compact>
  );
};
